import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Heart,
  MagnifyingGlass,
  Plus,
  Eye,
  CheckCircle
} from '@phosphor-icons/react';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

function CouplesWedding() {
  const styles = useThemeStyles();
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [selectedWedding, setSelectedWedding] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchWeddings();
  }, []);

  const fetchWeddings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/wedding/public-weddings');
      setWeddings(response.data.weddings || []);
    } catch (error) {
      setError('Failed to load weddings');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    if (!accessCode.trim()) {
      setError('Please enter an access code');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/api/wedding/request-access', {
        accessCode: accessCode.trim(),
        role: 'supplier',
        message: requestMessage.trim() || 'I would like to help with your wedding planning!'
      });
      
      setSuccess('Access request sent successfully! The couple will be notified.');
      setAccessCode('');
      setRequestMessage('');
      setShowRequestDialog(false);
      setSelectedWedding(null);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send access request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinWithCode = () => {
    setSelectedWedding(null);
    setShowRequestDialog(true);
  };

  const filteredWeddings = weddings.filter(wedding =>
    wedding.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wedding.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
          color: 'white',
        }}
      >
        <Box display="flex" alignItems="center" gap={3}>
          <Heart size={48} weight="regular" />
          <Box>
            <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, color: '#fff' }}>
              Couples Wedding
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Browse weddings and offer your services to couples
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Search and Join Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Join a Wedding with Access Code
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            If you have an access code from a couple, you can request access to their wedding planning.
          </Typography>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<Plus />}
            onClick={handleJoinWithCode}
            sx={{ mb: 2 }}
          >
            Join with Access Code
          </Button>
        </CardContent>
      </Card>

      {/* Search Bar */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search weddings by name or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlass size={20} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Error and Success Messages */}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      {/* Weddings Grid */}
      {filteredWeddings.length > 0 ? (
        <Grid container spacing={3}>
          {filteredWeddings.map((wedding) => (
            <Grid item xs={12} sm={6} md={4} key={wedding._id}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Heart size={24} weight="regular" color="#ff6b6b" />
                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                      {wedding.name}
                    </Typography>
                  </Box>
                  
                  {wedding.location && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      📍 {wedding.location}
                    </Typography>
                  )}
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {wedding.partnerDetails?.partnerOne?.name && wedding.partnerDetails?.partnerTwo?.name ? (
                      `${wedding.partnerDetails.partnerOne.name} & ${wedding.partnerDetails.partnerTwo.name}`
                    ) : (
                      'Couple details not available'
                    )}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip 
                      size="small" 
                      label={`${wedding.users?.length || 0} members`}
                      color="primary"
                      variant="outlined"
                    />
                    {wedding.supplierAccess?.length > 0 && (
                      <Chip 
                        size="small" 
                        label={`${wedding.supplierAccess.length} suppliers`}
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    startIcon={<Eye />}
                    onClick={() => {
                      setSelectedWedding(wedding);
                      setShowRequestDialog(true);
                    }}
                  >
                    View Details & Request Access
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Heart size={64} weight="regular" color="#ccc" />
            <Typography variant="h6" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              {searchTerm ? 'No weddings found' : 'No weddings available'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm 
                ? 'Try adjusting your search terms'
                : 'Check back later for new weddings or ask couples for their access code'
              }
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Request Access Dialog */}
      <Dialog 
        open={showRequestDialog} 
        onClose={() => setShowRequestDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedWedding ? `Request Access to ${selectedWedding.name}` : 'Join Wedding with Access Code'}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleRequestAccess}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {selectedWedding && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Wedding Details:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedWedding.name}
                    {selectedWedding.location && ` • ${selectedWedding.location}`}
                  </Typography>
                </Box>
              )}
              
              <TextField
                fullWidth
                label="Access Code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="Enter 6-digit access code"
                inputProps={{ maxLength: 6 }}
                required
              />
              
              <TextField
                fullWidth
                label="Message (Optional)"
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                placeholder="Tell the couple why you'd like to help with their wedding..."
                multiline
                rows={3}
                inputProps={{ maxLength: 500 }}
              />
              
              <Typography variant="caption" color="text.secondary">
                Your request will be sent to the couple for approval. They'll be notified of your request.
              </Typography>
            </Stack>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRequestDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleRequestAccess}
            variant="contained"
            disabled={submitting || !accessCode.trim()}
            startIcon={submitting ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {submitting ? 'Sending...' : 'Send Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CouplesWedding;

